"use client";

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import Link from "next/link";
import { usePathname } from "next/navigation";

import CustomImage from "@/components/shared/CustomImage";
import { Typography } from "@/components/shared/Typography";
import { SUBCATEGORY_PAGE_NEWS_LIMIT } from "@/data/constants";
import { fetchNewsBySubCategory } from "@/services/newsService";
import { ArticleSubCategoryType, ArticleType } from "@/types/article";
import { getImageUrl } from "@/utils/getImageUrl";
import { getNewsDetailsUrl } from "@/utils/getNewsDetailsUrl";
import TitleTag from "@/components/shared/TitleTag";
import { getLabel } from "@/data/labels";
import { getLocaleFromPathname } from "@/utils/getLocaleFromPath";
import { formatDate } from "@/utils/dateUtils";

interface ArticleListSectionProps {
  initialData: {
    docs: ArticleType[];
    totalDocs: number;
    limit: number;
    page: number;
  };
  subCategory: ArticleSubCategoryType;
  lang: string;
}

export default function SubCategoryArticleListSection({
  initialData,
  subCategory,
  lang,
}: ArticleListSectionProps) {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname);

  const [articles, setArticles] = useState<ArticleType[]>(initialData?.docs || []);
  const [filters, setFilters] = useState({
    limit: SUBCATEGORY_PAGE_NEWS_LIMIT,
    page: 1,
  });

  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: ["fetchNewsBySubCategory", filters.page, subCategory?.slug],
    enabled: filters.page > 1,
    queryFn: () =>
      fetchNewsBySubCategory({
        subCategorySlug: subCategory?.slug,
        categorySlug: subCategory?.categoryId?.slug,
        limit: filters?.limit,
        page: filters?.page,
        lang,
      }),
  });

  const handleLoadMore = () => {
    setFilters(prev => ({ ...prev, page: prev.page + 1 }));
  };

  useEffect(() => {
    if (data) {
      setArticles(prev => [...prev, ...(data?.data?.docs || [])]);
    }
  }, [data]);

  // Determine if we should show the Load More button
  const showLoadMore =
    articles?.length > 0 &&
    ((filters.page === 1 && initialData.totalDocs > articles.length) ||
      (data && data?.data?.totalDocs > articles.length));

  // useEffect(() => {
  //   setArticles(initialData?.docs || []);
  // }, [initialData]);

  return (
    <div>
      {articles?.map((article, index) => {
        return (
          <div
            key={index}
            className="flex flex-row items-start gap-6 pt-6 pb-6 border-b group first:pt-0"
          >
            <Link
              href={getNewsDetailsUrl(
                article?.categoryId,
                article?.subCategoryId,
                article?.slug,
                locale,
              )}
              className="relative w-[30%] min-w-[30%] md:w-[33%] md:min-w-[33%] aspect-[16/12] md:aspect-video rounded-md overflow-hidden"
              aria-label={`View news: ${article?.title}`}
            >
              <CustomImage
                src={getImageUrl(article?.image?.sizes?.ld?.filename || article?.image?.filename)}
                alt={article?.image?.alt || ""}
                className="object-cover w-full h-full img-zoom-hover"
              />
              {((article?.isLiveNews &&
                article?.publishedAt &&
                Date.now() - new Date(article.publishedAt).getTime() <= 12 * 60 * 60 * 1000) ||
                article?.isInPrint) && (
                <div className="absolute top-2 left-2 z-10 flex flex-wrap">
                  {article?.isLiveNews &&
                    article?.publishedAt &&
                    Date.now() - new Date(article.publishedAt).getTime() <=
                      12 * 60 * 60 * 1000 && <TitleTag labelKey="LIVE_NEWS" />}
                  {article?.isInPrint && <TitleTag labelKey="PRINT_EDITION" />}
                </div>
              )}
            </Link>
            <div>
              <Link
                href={getNewsDetailsUrl(
                  article?.categoryId,
                  article?.subCategoryId,
                  article?.slug,
                  locale,
                )}
                aria-label={`View news: ${article?.title}`}
              >
                <Typography
                  variant="h5"
                  className="line-clamp-3 md:line-clamp-2 font-semibold !text-base md:!text-[18px]"
                >
                  {article?.title}
                </Typography>
              </Link>
              <span className="block text-[#858585] text-sm mt-2 font-medium">
                {formatDate(article?.publishedAt, lang)}
              </span>
              <Link
                href={getNewsDetailsUrl(
                  article?.categoryId,
                  article?.subCategoryId,
                  article?.slug,
                  locale,
                )}
                aria-label={`View news: ${article?.title}`}
              >
                <Typography className="hidden mt-2 line-clamp-3 md:block" variant="body2">
                  {article?.summary}
                </Typography>
              </Link>
            </div>
          </div>
        );
      })}

      {showLoadMore && (
        <div className="flex items-center justify-center mt-8">
          <button
            className="load-more-btn"
            onClick={handleLoadMore}
            disabled={isFetching}
            aria-label={getLabel(lang, "LOAD_MORE")}
          >
            {getLabel(lang, "LOAD_MORE")}
          </button>
        </div>
      )}
    </div>
  );
}
